<template>
    <aside class="floating-quick-menu" :class="{ collapsed: isCollapsed }">
        <button v-if="!isCollapsed" type="button" class="quick-menu-close" :aria-label="t('quick_menu.close')"
            @click="setCollapsed(true)">
            <el-icon>
                <Close />
            </el-icon>
        </button>

        <button type="button" class="quick-menu-toggle"
            :aria-label="isCollapsed ? t('quick_menu.expand') : t('quick_menu.collapse')"
            @click="setCollapsed(!isCollapsed)">
            <el-icon>
                <ArrowLeft v-if="isCollapsed" />
                <ArrowRight v-else />
            </el-icon>
        </button>

        <div class="quick-menu-content">
            <header class="quick-menu-mobile-hero">
                <span class="quick-menu-mobile-logo" aria-hidden="true">
                    <img src="/images/logo-list.png" alt="" />
                </span>
                <span class="quick-menu-mobile-copy">
                    <small>CHINAONE</small>
                    <strong>{{ t('quick_menu.title') }}</strong>
                    <span>{{ t('quick_menu.subtitle') }}</span>
                </span>
            </header>

            <div class="quick-menu-mobile-section-title">
                <strong>{{ t('quick_menu.services_title') }}</strong>
                <span>{{ menuItems.length }}</span>
            </div>

            <nav class="quick-menu-links">
                <router-link v-for="(item, index) in menuItems" :key="item.key" :to="getQuickMenuTarget(item)"
                    :class="{ 'is-active': activeQuickMenuKey === item.key }" @click="setActiveQuickMenu(item.key)">
                    <span class="quick-menu-link-main">
                        <span class="quick-menu-index">{{ String(index + 1).padStart(2, '0') }}</span>
                        <span class="quick-menu-label">{{ t(`quick_menu.${item.key}`) }}</span>
                    </span>
                    <el-icon>
                        <ArrowRight />
                    </el-icon>
                </router-link>
            </nav>

            <div class="quick-menu-mobile-section-title quick-menu-contact-title">
                <strong>{{ t('quick_menu.contact_title') }}</strong>
            </div>

            <a v-if="isKorean" class="quick-contact youtube-contact" href="https://www.youtube.com/@china1kr"
                target="_blank" rel="noopener noreferrer">
                <span class="youtube-mark">▶</span>
                <span>{{ t('quick_menu.youtube') }}</span>
            </a>

            <a v-if="isKorean" class="quick-contact phone-contact" href="tel:070-8878-1239">
                <el-icon>
                    <PhoneFilled />
                </el-icon>
                <span>{{ t('quick_menu.consult_3pl') }} 070-8878-1239</span>
            </a>
            <a v-else class="quick-contact phone-contact" href="tel:070-8878-1239">
                <el-icon>
                    <PhoneFilled />
                </el-icon>
                <span>{{ t('quick_menu.consult_3pl') }} 070-8878-1239</span>
            </a>

            <template v-if="isKorean">
                <a class="quick-contact yellow-contact" href="https://pf.kakao.com/_xcxgBaxj" target="_blank"
                    rel="noopener noreferrer">
                    <el-icon>
                        <ChatDotRound />
                    </el-icon>
                    <span>{{ t('quick_menu.delivery_consult') }} 070-8848-1139</span>
                </a>
                <a class="quick-contact yellow-contact" href="https://pf.kakao.com/_qxbCtn" target="_blank"
                    rel="noopener noreferrer">
                    <el-icon>
                        <ChatDotRound />
                    </el-icon>
                    <span>{{ t('quick_menu.buying_consult') }} 070-8848-1136</span>
                </a>
            </template>

            <template v-else>
                <a class="quick-contact yellow-contact" href="tel:132-1004-0325">
                    <el-icon>
                        <PhoneFilled />
                    </el-icon>
                    <span>{{ t('quick_menu.consult_3pl') }} 132-1004-0325</span>
                </a>
                <a class="quick-contact wechat-contact" href="/images/wechat_qr.jpg" target="_blank"
                    rel="noopener noreferrer">
                    <el-icon>
                        <ChatDotRound />
                    </el-icon>
                    <span>{{ t('quick_menu.wechat') }}</span>
                </a>
            </template>
        </div>
    </aside>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowLeft, ArrowRight, ChatDotRound, Close, PhoneFilled } from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/user'

const { t, locale } = useI18n()
const userStore = useUserStore()
const isCollapsed = ref(localStorage.getItem('floatingQuickMenuCollapsed') === '1')
const activeQuickMenuKey = ref(localStorage.getItem('floatingQuickMenuActiveKey') || '')
const isKorean = computed(() => String(locale.value).toLowerCase().startsWith('ko'))

const menuItems = [
    {
        key: 'ecommerce_buy',
        path: '/delivery-precautions?type=buy&orderType=1&requestGroup=1&title=menu.buying_service_order',
    },
    {
        key: 'ecommerce_delivery',
        path: '/delivery-precautions?type=delivery&orderType=2&requestGroup=1&title=menu.delivery_service_order',
    },
    {
        key: 'lcl_buy',
        path: '/delivery-precautions?type=buy&orderType=3&requestGroup=1&title=menu.buying_service_order',
    },
    {
        key: 'lcl_delivery',
        path: '/delivery-precautions?type=delivery&orderType=4&requestGroup=1&title=menu.delivery_service_order',
    },
    {
        key: 'rocket_cn_buy',
        path: '/delivery-precautions?type=buy&orderType=5&requestGroup=2&title=menu.buying_service_order',
    },
    {
        key: 'rocket_cn_delivery',
        path: '/delivery-precautions?type=delivery&orderType=6&requestGroup=2&title=menu.delivery_service_order',
    },
    {
        key: 'rocket_kr_inbound',
        path: '/delivery-precautions?type=delivery&orderType=7&requestGroup=2&title=menu.delivery_service_order',
    },
    {
        key: 'china_3pl',
        path: '/stock-manage',
    },
    {
        key: 'korea_3pl',
        path: '/delivery-precautions?type=buy&orderType=12&requestGroup=3&title=menu.buy_kr_delivery',
    },
    {
        key: 'korea_3pl_outbound',
        path: '/delivery-precautions?type=buy&orderType=13&requestGroup=3&title=menu.inventory_dropshipping_kr_order',
    },
    {
        key: 'coupang_return',
        path: '/delivery-application?type=3&ordertype=14&centerseq=6&nationid=1&dlvrwaycd=2&reqgroup=7',
    },
    { key: 'inventory_outbound_records', path: '/orders?scope=client_order_ko&state_seq=49' },
    { key: 'batch_order', path: '/bulk-excel?orderType=13' },
    { key: 'china_order_status', path: '/orders?scope=client_order_cn' },
    { key: 'korea_order_status', path: '/orders?scope=client_order_ko' },
]

const getQuickMenuTarget = (item) => {
    if (!userStore.userInfo?.token) return '/login'

    const [path, queryString = ''] = item.path.split('?')
    const params = new URLSearchParams(queryString)
    params.set('quickTitle', `quick_menu.${item.key}`)
    return `${path}?${params.toString()}`
}

const setCollapsed = (value) => {
    isCollapsed.value = value
    localStorage.setItem('floatingQuickMenuCollapsed', value ? '1' : '0')
}

const setActiveQuickMenu = (key) => {
    activeQuickMenuKey.value = key
    localStorage.setItem('floatingQuickMenuActiveKey', key)
}
</script>

<style scoped>
.floating-quick-menu {
    position: fixed;
    z-index: 1100;
    top: 50%;
    right: 0;
    width: 260px;
    max-height: calc(100vh - 145px);
    border: 1px solid #dfe5ef;
    border-right: 0;
    background: #fff;
    box-shadow: 0 6px 20px rgba(31, 41, 55, .18);
    transform: translateY(-50%);
    transition: transform .22s ease;
}

.floating-quick-menu.collapsed {
    transform: translate(100%, -50%);
}

.quick-menu-content {
    max-height: calc(100vh - 145px);
    overflow-y: auto;
    padding-top: 30px;
}

.quick-menu-mobile-hero,
.quick-menu-mobile-section-title {
    display: none;
}

.quick-menu-close {
    position: absolute;
    z-index: 2;
    top: 6px;
    right: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #303133;
    cursor: pointer;
}

.quick-menu-toggle {
    position: absolute;
    top: 50%;
    left: -34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 52px;
    padding: 0;
    border: 0;
    border-radius: 10px 0 0 10px;
    background: #303030;
    box-shadow: 0 3px 10px rgba(0, 0, 0, .25);
    color: #fff;
    cursor: pointer;
    transform: translateY(-50%);
}

.quick-menu-links {
    padding: 2px 12px 10px;
}

.quick-menu-links a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 30px;
    padding: 4px 7px;
    border-bottom: 1px solid #f1f3f6;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.35;
    text-decoration: none;
}

.quick-menu-links a:hover {
    background: #f2f7ff;
    color: #e94d4d;
}

.quick-menu-links a.is-active {
    background: #fff6f6;
    color: #e94d52;
    font-weight: 600;
}

.quick-menu-link-main {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
}

.quick-menu-index {
    display: inline-flex;
    width: 30px;
    height: 22px;
    flex: 0 0 30px;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    background: #f0f3f7;
    color: #7a8797;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
}

.quick-menu-label {
    min-width: 0;
}

.quick-menu-links a:hover .quick-menu-index,
.quick-menu-links a.is-active .quick-menu-index {
    color: #e94d52;
}

.quick-menu-links a:hover .quick-menu-index {
    background: #fff0f1;
}

.quick-menu-links a.is-active .quick-menu-index {
    background: #ffdfe3;
}

.quick-menu-links a.is-active .el-icon {
    color: #e94d52;
}

.quick-menu-links a .el-icon {
    flex: 0 0 auto;
    color: #909399;
    font-size: 12px;
}

.quick-contact {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 43px;
    padding: 8px 14px;
    color: #222;
    font-size: 12px;
    font-weight: 600;
    text-decoration: none;
}

.quick-contact .el-icon {
    flex: 0 0 auto;
    font-size: 21px;
}

.phone-contact {
    background: #e5e5e5;
}

.yellow-contact {
    background: #ffe500;
    border-top: 1px solid rgba(0, 0, 0, .08);
}

.wechat-contact {
    background: #08b844;
    color: #fff;
}

.youtube-contact {
    background: #e5e5e5;
}

.youtube-mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 29px;
    height: 20px;
    border-radius: 6px;
    background: #f00000;
    color: #fff;
    font-size: 10px;
}

@media (max-width: 991px) {
    .floating-quick-menu {
        top: 0;
        right: 0;
        display: block;
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        max-height: none;
        overflow: visible;
        border: 0;
        border-radius: 0;
        background: #f4f8fb;
        transform: none;
    }

    .floating-quick-menu.collapsed {
        width: 0;
        transform: none;
        box-shadow: none;
    }

    .floating-quick-menu.collapsed .quick-menu-content {
        display: none;
    }

    .quick-menu-content {
        height: 100%;
        max-height: none;
        padding: max(18px, env(safe-area-inset-top)) 0 max(24px, env(safe-area-inset-bottom));
        background:
            radial-gradient(circle at 92% 3%, rgba(238, 69, 62, .09), transparent 22%),
            linear-gradient(180deg, #f8fbfd 0%, #f1f6fa 100%);
        overscroll-behavior: contain;
    }

    .quick-menu-close {
        top: max(14px, env(safe-area-inset-top));
        right: 16px;
        width: 44px;
        height: 44px;
        border: 1px solid #d8e2eb;
        border-radius: 13px;
        background: #fff;
        box-shadow: 0 6px 18px rgba(42, 62, 82, .14);
        color: #34495e;
        font-size: 23px;
    }

    .quick-menu-mobile-hero {
        position: relative;
        display: flex;
        min-height: 112px;
        margin: 0 14px 16px;
        padding: 22px 62px 20px 18px;
        overflow: hidden;
        align-items: center;
        gap: 12px;
        border: 1px solid rgba(221, 230, 238, .9);
        border-radius: 20px;
        background: rgba(255, 255, 255, .9);
        box-shadow: 0 12px 32px rgba(42, 62, 82, .08);
    }

    .quick-menu-mobile-hero::after {
        position: absolute;
        right: -48px;
        bottom: -64px;
        width: 142px;
        height: 142px;
        border: 1px solid rgba(238, 69, 62, .12);
        border-radius: 50%;
        box-shadow: 0 0 0 18px rgba(83, 143, 196, .035);
        content: "";
        pointer-events: none;
    }

    .quick-menu-mobile-logo {
        display: inline-flex;
        width: 58px;
        height: 52px;
        flex: 0 0 58px;
        align-items: center;
        justify-content: center;
    }

    .quick-menu-mobile-logo img {
        width: 58px;
        height: 52px;
        max-width: none;
        object-fit: contain;
    }

    .quick-menu-mobile-copy {
        display: flex;
        min-width: 0;
        flex-direction: column;
    }

    .quick-menu-mobile-copy small {
        margin-bottom: 2px;
        color: #ee453e;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 1.6px;
    }

    .quick-menu-mobile-copy strong {
        color: #24364a;
        font-size: 20px;
        line-height: 28px;
    }

    .quick-menu-mobile-copy > span {
        margin-top: 3px;
        color: #8493a3;
        font-size: 12px;
        line-height: 18px;
    }

    .quick-menu-mobile-section-title {
        display: flex;
        margin: 0 18px 9px;
        align-items: center;
        justify-content: space-between;
        color: #34475b;
        font-size: 14px;
    }

    .quick-menu-mobile-section-title strong {
        position: relative;
        padding-left: 11px;
    }

    .quick-menu-mobile-section-title strong::before {
        position: absolute;
        top: 2px;
        bottom: 2px;
        left: 0;
        width: 3px;
        border-radius: 3px;
        background: #ef4b50;
        content: "";
    }

    .quick-menu-mobile-section-title > span {
        display: inline-flex;
        min-width: 28px;
        height: 22px;
        padding: 0 8px;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        background: #eaf1f6;
        color: #66798d;
        font-size: 11px;
        font-weight: 700;
    }

    .quick-menu-links {
        margin: 0 14px;
        padding: 7px 12px;
        overflow: hidden;
        border: 1px solid #e1e9f0;
        border-radius: 18px;
        background: rgba(255, 255, 255, .94);
        box-shadow: 0 10px 28px rgba(42, 62, 82, .065);
    }

    .quick-menu-links a {
        min-height: 43px;
        padding: 8px 5px;
        color: #3d5064;
        font-size: 13px;
    }

    .quick-menu-links a:last-child {
        border-bottom: 0;
    }

    .quick-menu-links a .el-icon {
        width: 25px;
        height: 25px;
        border-radius: 8px;
        background: #f1f6fa;
        color: #7890a5;
    }

    .quick-menu-contact-title {
        margin-top: 20px;
    }

    .quick-contact {
        min-height: 52px;
        margin: 0 14px 9px;
        padding: 10px 15px;
        border: 1px solid transparent;
        border-radius: 14px;
        box-shadow: 0 8px 20px rgba(42, 62, 82, .08);
    }

    .phone-contact,
    .youtube-contact {
        border-color: #dbe5ed;
        background: linear-gradient(135deg, #263a50, #354d64);
        color: #fff;
    }

    .yellow-contact {
        border-color: #f1dea0;
        background: linear-gradient(135deg, #fff7d8, #ffe680);
        color: #473b12;
    }

    .wechat-contact {
        border-color: rgba(8, 184, 68, .2);
        background: linear-gradient(135deg, #0cbf53, #079d43);
        color: #fff;
    }

    .floating-quick-menu:not(.collapsed) .quick-menu-toggle {
        display: none;
    }

    .quick-menu-toggle {
        left: -40px;
        width: 40px;
        height: 56px;
    }

}

@media (max-width: 380px) {
    .quick-menu-toggle {
        left: -36px;
        width: 36px;
    }

    .quick-menu-links {
        padding-right: 8px;
        padding-left: 8px;
    }

    .quick-menu-links a {
        font-size: 12px;
    }

    .quick-contact {
        padding-right: 10px;
        padding-left: 10px;
    }
}
</style>
